import React, { useMemo, useState } from "react"
import { motion } from "framer-motion"
import ModalShell from "../ModalShell"
import ColumnTemplatesPanel from "../ColumnTemplatesPanel"

const ColumnSettingsModal = ({
  // существующие пропсы
  columns,
  order,
  visible,
  onApply,
  onClose,
  // шаблоны
  isAdmin,
  templates,
  departments,
  appliedTemplateId,
  onApplyTemplate,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
}) => {
  const [tab, setTab] = useState("columns") // "columns" | "templates"

  // Шаблон задаёт общий порядок/видимость для всех. Личное перетаскивание
  // в этой модалке — персональная надстройка поверх него, только для
  // текущего пользователя (сохраняется через onApply, а не через
  // onCreateTemplate/onUpdateTemplate).
  const appliedTemplate = useMemo(() => {
    if (appliedTemplateId == null) return null
    return (templates ?? []).find((t) => String(t.id) === String(appliedTemplateId)) ?? null
  }, [templates, appliedTemplateId])

  const baseOrder = appliedTemplate?.order ?? columns.map((c) => c.id)

  // ── Состояние колонок ──
  // lv больше не редактируется в этой модалке (видимость только из
  // шаблона), поэтому без сеттера — используем как read-only подсказку.
  const [lo, setLo] = useState([...order])
  const [lv] = useState([...visible])
  const [drag, setDrag] = useState(null)
  const [over, setOver] = useState(null)

  const drop = (tid) => {
    if (!drag || drag === tid) {
      setDrag(null)
      setOver(null)
      return
    }
    const a = [...lo]
    const fi = a.indexOf(drag)
    const ti = a.indexOf(tid)
    a.splice(fi, 1)
    a.splice(ti, 0, drag)
    setLo(a)
    setDrag(null)
    setOver(null)
  }

  const apply = () => {
    // Видимость колонок настраивается только через шаблоны — на бэк
    // уходит только персональный порядок.
    onApply(lo)
    onClose()
  }

  return (
    <ModalShell onClose={onClose} zIndex={200} maxWidth="sm:max-w-md">
      <div className="bg-white w-full rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col min-h-0 flex-1">
        {/* ── Заголовок ── */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Настройка колонок</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {tab === "columns"
                ? "Перетащите, чтобы изменить порядок колонок"
                : "Какие колонки показывать — выбирается в шаблоне"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex border-b border-gray-100 flex-shrink-0">
          <button
            onClick={() => setTab("columns")}
            className={`flex-1 py-2.5 text-xs font-medium transition-colors border-b-2
              ${
                tab === "columns"
                  ? "text-blue-600 border-blue-600"
                  : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
              }`}
          >
            Колонки
          </button>
          <button
            onClick={() => setTab("templates")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2
              ${
                tab === "templates"
                  ? "text-blue-600 border-blue-600"
                  : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
              }`}
          >
            Шаблоны
            {templates?.length > 0 && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                ${tab === "templates" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}
              >
                {templates.length}
              </span>
            )}
          </button>
        </div>

        {tab === "columns" && (
          <div className="px-4 pt-3 flex-shrink-0">
            <p className="text-[11px] text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
              Порядок здесь сохраняется только для вас. Общий порядок и набор колонок для всех
              задаются в применённом шаблоне.
            </p>
          </div>
        )}

        {tab === "columns" && (
          <div className="p-3 overflow-y-auto flex-1">
            {lo.map((colId) => {
              const col = columns.find((c) => c.id === colId)
              if (!col) return null
              const isV = lv.includes(colId)
              return (
                <motion.div
                  key={colId}
                  layout
                  draggable={!col.fixed}
                  onDragStart={() => setDrag(colId)}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setOver(colId)
                  }}
                  onDrop={() => drop(colId)}
                  onDragEnd={() => {
                    setDrag(null)
                    setOver(null)
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-colors select-none
                    ${over === colId ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"}
                    ${drag === colId ? "opacity-40" : ""}
                    ${col.fixed ? "" : "cursor-grab active:cursor-grabbing"}`}
                >
                  <div className={`flex-shrink-0 ${col.fixed ? "opacity-20" : "text-gray-300"}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8h16M4 16h16"
                      />
                    </svg>
                  </div>
                  <span className={`text-sm flex-1 ${isV ? "text-gray-800" : "text-gray-400"}`}>
                    {col.label}
                  </span>
                  {!isV && <span className="text-[10px] text-gray-300">скрыта в шаблоне</span>}
                  {col.fixed && <span className="text-xs text-gray-300">фикс.</span>}
                </motion.div>
              )
            })}
          </div>
        )}

        {/* ── Содержимое вкладки Шаблоны ── */}
        {tab === "templates" && (
          <div className="p-4 overflow-y-auto flex-1">
            <ColumnTemplatesPanel
              isAdmin={isAdmin}
              columns={columns}
              visibleCols={lv}
              colOrder={lo}
              templates={templates ?? []}
              departments={departments ?? []}
              appliedTemplateId={appliedTemplateId}
              onApplyTemplate={(template) => {
                // Синхронизируем локальное состояние вкладки Колонки
                setLo(template.order)
                onApplyTemplate(template)
                onClose()
              }}
              onCreateTemplate={onCreateTemplate}
              onUpdateTemplate={onUpdateTemplate}
              onDeleteTemplate={onDeleteTemplate}
            />
          </div>
        )}

        {tab === "columns" && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
            <button
              onClick={() => {
                // Сбрасываем личный порядок обратно к базовому — тому,
                // что задан применённым шаблоном (а не к произвольному
                // порядку из общего списка колонок).
                setLo([...baseOrder])
              }}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Сбросить порядок
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-all"
              >
                Отмена
              </button>
              <button
                onClick={apply}
                className="px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all shadow-sm shadow-blue-200"
              >
                Применить
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  )
}

export default ColumnSettingsModal
